import React, { useState } from 'react';
import {
  FileJson,
  MapPin,
  Square,
  Minus,
  Trees,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  AlertCircle,
  Info,
  Languages
} from 'lucide-react';

const CodeBlock = ({ code, language = 'json' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copiar código"
      >
        {copied ? (
          <Check className="w-4 h-4 text-green-400" />
        ) : (
          <Copy className="w-4 h-4 text-gray-300" />
        )}
      </button>
    </div>
  );
};

const Accordion = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
          <span className="font-semibold text-gray-900 dark:text-gray-100">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-500" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-white dark:bg-gray-900">
          {children}
        </div>
      )}
    </div>
  );
};

const ColumnAliases = ({ aliases }) => (
  <div className="flex flex-wrap gap-1">
    {aliases.map((alias, i) => (
      <code key={i} className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded text-xs font-mono">
        {alias}
      </code>
    ))}
  </div>
);

const FieldTable = ({ fields }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-gray-100 dark:bg-gray-800">
          <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-gray-100">Columna</th>
          <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-gray-100">Nombres Alternativos</th>
          <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-gray-100">Tipo</th>
          <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-gray-100">Descripción</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
        {fields.map((field, index) => (
          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
            <td className="px-4 py-3">
              <code className="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-xs font-mono font-bold">
                {field.name}
              </code>
            </td>
            <td className="px-4 py-3">
              {field.aliases && field.aliases.length > 0 ? (
                <ColumnAliases aliases={field.aliases} />
              ) : (
                <span className="text-gray-400 dark:text-gray-500 italic text-xs">-</span>
              )}
            </td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{field.type}</td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{field.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ValueTable = ({ title, values, colorDot = false }) => (
  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
    <h5 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">{title}</h5>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Valor del Sistema</th>
            <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Valores Aceptados (puedes usar cualquiera)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {values.map((item, index) => (
            <tr key={index}>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  {colorDot && item.color && (
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }}></span>
                  )}
                  <code className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 px-2 py-0.5 rounded text-xs font-mono font-bold">
                    {item.value}
                  </code>
                </div>
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {item.aliases.map((alias, i) => (
                    <span key={i} className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded text-xs">
                      {alias}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

function GeoJSONDocumentation() {
  // Campos para Lotes con aliases
  const lotFields = [
    { name: 'lot_number', aliases: ['numero_lote', 'nro_lote', 'num_lote', 'lote_numero'], type: 'Texto', description: 'Número o código único del lote (ej: "A-001", "LOTE-15")' },
    { name: 'name', aliases: ['nombre', 'titulo'], type: 'Texto', description: 'Nombre descriptivo del lote' },
    { name: 'description', aliases: ['descripcion', 'desc', 'detalle'], type: 'Texto', description: 'Descripción detallada del lote' },
    { name: 'address', aliases: ['direccion', 'ubicacion', 'location'], type: 'Texto', description: 'Dirección o ubicación del lote' },
    { name: 'typology', aliases: ['tipologia', 'tipo_lote', 'tipo'], type: 'Texto', description: 'Tipología del lote (ej: "Residencial", "Comercial")' },
    { name: 'price', aliases: ['precio', 'costo', 'valor', 'cost'], type: 'Número', description: 'Precio del lote en la moneda configurada' },
    { name: 'price_per_sqm', aliases: ['precio_m2', 'precio_por_m2', 'price_sqm'], type: 'Número', description: 'Precio por metro cuadrado' },
    { name: 'area_declared', aliases: ['area', 'superficie', 'area_declarada', 'metros', 'size'], type: 'Número', description: 'Área declarada en metros cuadrados' },
    { name: 'perimeter', aliases: ['perimetro'], type: 'Número', description: 'Perímetro en metros' },
    { name: 'front_meters', aliases: ['frente', 'metros_frente', 'front'], type: 'Número', description: 'Metros de frente del lote' },
    { name: 'depth_meters', aliases: ['fondo', 'metros_fondo', 'depth', 'profundidad'], type: 'Número', description: 'Metros de fondo del lote' },
    { name: 'corner_lot', aliases: ['esquinero', 'esquina', 'lote_esquinero', 'corner'], type: 'Booleano', description: 'Indica si es lote esquinero' },
    { name: 'block', aliases: ['manzana', 'bloque', 'mz'], type: 'Texto', description: 'Manzana o bloque del lote' },
    { name: 'stage', aliases: ['etapa', 'fase', 'sector', 'phase'], type: 'Texto', description: 'Etapa del proyecto' },
    { name: 'status', aliases: ['estado', 'state'], type: 'Enum', description: 'Estado actual del lote (ver tabla de valores)' },
    { name: 'legal_paper', aliases: ['documento_legal', 'documento', 'titulo', 'paper'], type: 'Enum', description: 'Tipo de documento legal (ver tabla de valores)' },
  ];

  // Valores de status con aliases
  const statusValues = [
    { value: 'DISPONIBLE', color: '#0046FF', aliases: ['disponible', 'available', 'libre', 'free', 'for_sale', 'a la venta', 'activo', 'active'] },
    { value: 'RESERVADO', color: '#FFC107', aliases: ['reservado', 'reserved', 'apartado', 'separado', 'en reserva', 'hold', 'pending', 'pendiente'] },
    { value: 'EN_VENTA', color: '#FF6F3C', aliases: ['en_venta', 'en venta', 'venta', 'en proceso', 'selling', 'in_sale', 'processing'] },
    { value: 'VENDIDO', color: '#ED3F27', aliases: ['vendido', 'sold', 'cerrado', 'completado', 'completed', 'closed', 'finalizado'] },
    { value: 'NO_DISPONIBLE', color: '#FDF4E3', aliases: ['no_disponible', 'no disponible', 'unavailable', 'bloqueado', 'blocked', 'inactivo', 'cancelado'] },
    { value: 'DEVOLUCION', color: '#9B5DE0', aliases: ['devolucion', 'devolución', 'return', 'returned', 'devuelto', 'reembolso', 'refund'] },
  ];

  // Valores de legal_paper con aliases
  const legalPaperValues = [
    { value: 'TITULO_PROPIEDAD', aliases: ['titulo_propiedad', 'titulo de propiedad', 'property title', 'titulo', 'escritura', 'deed'] },
    { value: 'TRANSFERENCIA_NOTARIAL', aliases: ['transferencia_notarial', 'transferencia notarial', 'notarial transfer', 'notarial', 'transferencia'] },
    { value: 'CERTIFICADO_POSESION', aliases: ['certificado_posesion', 'certificado de posesion', 'possession certificate', 'posesion', 'constancia de posesion'] },
    { value: 'ADJUDICACION_MUNICIPAL', aliases: ['adjudicacion_municipal', 'adjudicacion municipal', 'municipal adjudication', 'adjudicacion', 'municipal'] },
    { value: 'TITULO_COFOPRI', aliases: ['titulo_cofopri', 'titulo cofopri', 'cofopri', 'cofopri title'] },
    { value: 'RESOLUCION_SANEAMIENTO', aliases: ['resolucion_saneamiento', 'resolucion de saneamiento', 'saneamiento', 'saneamiento fisico legal'] },
    { value: 'OTRO', aliases: ['otro', 'other', 'otros', 'n/a', 'ninguno', 'none', 'sin documento', 'pendiente'] },
  ];

  // Campos para Marcadores con aliases
  const markerFields = [
    { name: 'name', aliases: ['nombre', 'titulo'], type: 'Texto', description: 'Nombre del marcador' },
    { name: 'description', aliases: ['descripcion', 'desc', 'detalle'], type: 'Texto', description: 'Descripción del marcador' },
    { name: 'marker_type', aliases: ['tipo', 'type', 'tipo_marcador'], type: 'Enum', description: 'Tipo de marcador (ver tabla de valores)' },
    { name: 'color', aliases: [], type: 'Texto (Hex)', description: 'Color en formato hexadecimal (ej: #3B82F6) o nombre de color' },
    { name: 'icon', aliases: ['icono'], type: 'Texto', description: 'Icono del marcador (usa los mismos valores que marker_type)' },
  ];

  // Valores de marker_type con aliases
  const markerTypeValues = [
    { value: 'entrada', color: '#22C55E', aliases: ['entrada', 'entrance', 'ingreso', 'entry', 'puerta', 'gate', 'acceso', 'access', 'entrada principal'] },
    { value: 'salida', color: '#EF4444', aliases: ['salida', 'exit', 'egreso', 'salida principal', 'out'] },
    { value: 'etapa', color: '#3B82F6', aliases: ['etapa', 'stage', 'phase', 'fase', 'sector', 'zona', 'zone'] },
    { value: 'bloque', color: '#8B5CF6', aliases: ['bloque', 'manzana', 'block', 'manz', 'mz', 'cuadra', 'lote', 'lot'] },
    { value: 'arboles', color: '#16A34A', aliases: ['arboles', 'arbol', 'tree', 'trees', 'area verde', 'verde', 'green', 'vegetacion', 'jardin', 'parque', 'park'] },
    { value: 'estacionamiento', color: '#6B7280', aliases: ['estacionamiento', 'parking', 'parqueo', 'garage', 'garaje', 'cochera', 'parking_lot', 'car park'] },
    { value: 'punto_interes', color: '#F97316', aliases: ['punto_interes', 'punto de interes', 'poi', 'landmark', 'referencia', 'ubicacion', 'punto', 'marker', 'marcador', 'lugar'] },
    { value: 'punto_foto', color: '#EC4899', aliases: ['punto_foto', 'punto foto', 'photo_point', 'foto', 'fotos', 'photo', 'imagen', 'image', 'galeria', 'camera', 'viewpoint', 'vista'] },
  ];

  // Campos para Líneas con aliases
  const lineFields = [
    { name: 'name', aliases: ['nombre', 'titulo'], type: 'Texto', description: 'Nombre de la línea' },
    { name: 'description', aliases: ['descripcion', 'desc', 'detalle'], type: 'Texto', description: 'Descripción de la línea' },
    { name: 'line_type', aliases: ['tipo', 'type', 'tipo_linea', 'highway'], type: 'Enum', description: 'Tipo de línea (ver tabla de valores)' },
    { name: 'color', aliases: [], type: 'Texto (Hex)', description: 'Color en formato hexadecimal o nombre de color' },
    { name: 'width', aliases: ['ancho', 'ancho_metros'], type: 'Número', description: 'Ancho en metros (opcional)' },
    { name: 'stroke_width', aliases: ['grosor', 'ancho_trazo', 'stroke'], type: 'Número', description: 'Ancho de trazo en píxeles (1-20)' },
    { name: 'line_style', aliases: ['estilo', 'style', 'estilo_linea'], type: 'Enum', description: 'Estilo: solid, dashed, dotted' },
  ];

  // Valores de line_type con aliases
  const lineTypeValues = [
    { value: 'CALLE', color: '#6B7280', aliases: ['calle', 'street', 'road', 'via', 'vía', 'camino', 'carretera', 'highway'] },
    { value: 'VEREDA', color: '#9CA3AF', aliases: ['vereda', 'sidewalk', 'acera', 'banqueta', 'footpath'] },
    { value: 'SENDERO', color: '#A3E635', aliases: ['sendero', 'path', 'trail', 'senda', 'trocha'] },
    { value: 'PASAJE', color: '#D1D5DB', aliases: ['pasaje', 'passage', 'callejon', 'callejón', 'alley'] },
    { value: 'AVENIDA', color: '#4B5563', aliases: ['avenida', 'avenue', 'av', 'av.', 'avda', 'boulevard', 'blvd'] },
    { value: 'LIMITE', color: '#EF4444', aliases: ['limite', 'límite', 'boundary', 'border', 'lindero', 'perimetro', 'perímetro', 'perimeter'] },
    { value: 'CERCA', color: '#92400E', aliases: ['cerca', 'fence', 'cerco', 'reja', 'valla'] },
    { value: 'MURO', color: '#1F2937', aliases: ['muro', 'wall', 'pared', 'tapia', 'barda'] },
    { value: 'LINEA_CANCHA_FUTBOL', color: '#FFFFFF', aliases: ['linea_cancha_futbol', 'linea futbol', 'soccer line', 'football line', 'futbol', 'fútbol', 'soccer'] },
    { value: 'LINEA_CANCHA_BASQUET', color: '#F97316', aliases: ['linea_cancha_basquet', 'linea basquet', 'basketball line', 'basquet', 'básquet', 'basketball'] },
    { value: 'LINEA_CANCHA_TENIS', color: '#FFFFFF', aliases: ['linea_cancha_tenis', 'linea tenis', 'tennis line', 'tenis', 'tennis'] },
    { value: 'LINEA_CANCHA_VOLEY', color: '#3B82F6', aliases: ['linea_cancha_voley', 'linea voley', 'volleyball line', 'voley', 'vóley', 'volleyball'] },
    { value: 'LINEA_CANCHA_GENERAL', color: '#FFFFFF', aliases: ['linea_cancha_general', 'linea cancha', 'court line', 'sports line', 'cancha', 'deportiva'] },
    { value: 'TUBERIA', color: '#06B6D4', aliases: ['tuberia', 'tubería', 'pipe', 'pipeline', 'tubo', 'ducto', 'agua', 'desague', 'desagüe'] },
    { value: 'CABLE', color: '#EAB308', aliases: ['cable', 'wire', 'electrico', 'eléctrico', 'electric', 'linea electrica', 'power line'] },
    { value: 'OTRO', color: '#6B7280', aliases: ['otro', 'other', 'otros', 'desconocido', 'unknown'] },
  ];

  // Campos para Áreas Recreativas con aliases
  const areaFields = [
    { name: 'name', aliases: ['nombre', 'titulo'], type: 'Texto', description: 'Nombre del área' },
    { name: 'description', aliases: ['descripcion', 'desc', 'detalle'], type: 'Texto', description: 'Descripción del área' },
    { name: 'area_type', aliases: ['tipo', 'type', 'tipo_area', 'landuse'], type: 'Enum', description: 'Tipo de área recreativa (ver tabla de valores)' },
    { name: 'area_declared', aliases: ['area', 'superficie', 'metros', 'size'], type: 'Número', description: 'Área declarada en m²' },
    { name: 'color', aliases: [], type: 'Texto (Hex)', description: 'Color del área en formato hexadecimal o nombre de color' },
  ];

  // Valores de area_type con aliases
  const areaTypeValues = [
    { value: 'AREA_VERDE', aliases: ['area_verde', 'area verde', 'green area', 'green', 'jardin', 'jardín', 'garden', 'cesped', 'grass', 'lawn'] },
    { value: 'PARQUE', aliases: ['parque', 'park', 'plaza', 'square', 'plazuela'] },
    { value: 'CANCHA_DEPORTIVA', aliases: ['cancha_deportiva', 'cancha deportiva', 'cancha', 'court', 'sports court', 'losa deportiva', 'deportivo', 'sports'] },
    { value: 'PISCINA', aliases: ['piscina', 'pool', 'swimming pool', 'swimming_pool', 'alberca'] },
    { value: 'ZONA_JUEGOS', aliases: ['zona_juegos', 'zona juegos', 'playground', 'play area', 'juegos', 'games', 'juegos infantiles'] },
    { value: 'AREA_PICNIC', aliases: ['area_picnic', 'area picnic', 'picnic area', 'picnic', 'merendero'] },
    { value: 'PARQUE_INFANTIL', aliases: ['parque_infantil', 'parque infantil', 'children park', 'kids park', 'infantil'] },
    { value: 'LAGUNA', aliases: ['laguna', 'lake', 'pond', 'laguna artificial', 'lago', 'estanque'] },
    { value: 'CLUB_HOUSE', aliases: ['club_house', 'club house', 'clubhouse', 'casa club', 'club', 'gimnasio', 'gym'] },
    { value: 'SALON_EVENTOS', aliases: ['salon_eventos', 'salon eventos', 'event hall', 'salon', 'hall', 'salon comunal', 'sum'] },
    { value: 'ZONA_BBQ', aliases: ['zona_bbq', 'zona bbq', 'bbq', 'bbq area', 'barbecue', 'parrilla', 'asador', 'grill'] },
    { value: 'QUINCHO', aliases: ['quincho', 'barbecue area', 'parrillera', 'terraza bbq'] },
    { value: 'GUARDERIA', aliases: ['guarderia', 'guardería', 'daycare', 'nursery', 'kindergarten', 'jardin infantil'] },
    { value: 'MINIMARKET', aliases: ['minimarket', 'mini market', 'tienda', 'store', 'shop', 'bodega', 'convenience store'] },
    { value: 'ESTACIONAMIENTO', aliases: ['estacionamiento', 'parking', 'parking lot', 'parqueo', 'garage', 'garaje', 'cochera', 'car park'] },
    { value: 'ETAPA', aliases: ['etapa', 'stage', 'phase', 'fase', 'sector', 'seccion', 'section'] },
    { value: 'COLEGIO', aliases: ['colegio', 'school', 'escuela', 'centro educativo'] },
    { value: 'INSTITUCION_EDUCATIVA', aliases: ['institucion_educativa', 'institucion educativa', 'educational institution', 'universidad', 'university', 'instituto'] },
    { value: 'CENTRO_SALUD', aliases: ['centro_salud', 'centro salud', 'health center', 'clinic', 'clinica', 'posta', 'hospital', 'medical'] },
    { value: 'ESPACIO_PUBLICO', aliases: ['espacio_publico', 'espacio publico', 'public space', 'public area', 'reserva', 'mirador', 'viewpoint', 'pasaje', 'alameda'] },
  ];

  // Valores de colores con aliases
  const colorValues = [
    { value: '#EF4444', aliases: ['rojo', 'red'] },
    { value: '#22C55E', aliases: ['verde', 'green'] },
    { value: '#3B82F6', aliases: ['azul', 'blue'] },
    { value: '#EAB308', aliases: ['amarillo', 'yellow'] },
    { value: '#F97316', aliases: ['naranja', 'orange'] },
    { value: '#A855F7', aliases: ['morado', 'purple'] },
    { value: '#EC4899', aliases: ['rosa', 'pink'] },
    { value: '#06B6D4', aliases: ['cyan', 'cian', 'turquesa', 'teal'] },
    { value: '#6B7280', aliases: ['gris', 'gray', 'grey'] },
    { value: '#1F2937', aliases: ['negro', 'black'] },
    { value: '#F9FAFB', aliases: ['blanco', 'white'] },
    { value: '#92400E', aliases: ['marron', 'brown', 'cafe'] },
  ];

  // Valores booleanos
  const booleanValues = [
    { value: 'true', aliases: ['true', 'yes', 'si', 'sí', '1', 'verdadero', 'activo', 'active', 'on', 'habilitado', 'tiene', 'con', 'x'] },
    { value: 'false', aliases: ['false', 'no', '0', 'falso', 'inactivo', 'inactive', 'off', 'deshabilitado', 'sin'] },
  ];

  // Ejemplos de GeoJSON
  const lotExample = `{
  "type": "FeatureCollection",
  "features": [
    {
      "type": "Feature",
      "properties": {
        "numero_lote": "A-001",
        "nombre": "Lote Esquinero Premium",
        "precio": 25000,
        "area": 150,
        "estado": "disponible",
        "esquinero": "si",
        "manzana": "A",
        "etapa": "Etapa 1"
      },
      "geometry": {
        "type": "Polygon",
        "coordinates": [[
          [-76.123456, -12.045678],
          [-76.123456, -12.046789],
          [-76.124567, -12.046789],
          [-76.124567, -12.045678],
          [-76.123456, -12.045678]
        ]]
      }
    }
  ]
}`;

  const markerExample = `{
  "type": "FeatureCollection",
  "features": [
    {
      "type": "Feature",
      "properties": {
        "nombre": "Entrada Principal",
        "descripcion": "Acceso vehicular",
        "tipo": "entrada",
        "color": "verde"
      },
      "geometry": {
        "type": "Point",
        "coordinates": [-76.123456, -12.045678]
      }
    }
  ]
}`;

  const lineExample = `{
  "type": "FeatureCollection",
  "features": [
    {
      "type": "Feature",
      "properties": {
        "nombre": "Calle Principal",
        "tipo": "calle",
        "color": "gris",
        "grosor": 3,
        "estilo": "solid"
      },
      "geometry": {
        "type": "LineString",
        "coordinates": [
          [-76.123456, -12.045678],
          [-76.124567, -12.045678],
          [-76.125678, -12.046789]
        ]
      }
    }
  ]
}`;

  const areaExample = `{
  "type": "FeatureCollection",
  "features": [
    {
      "type": "Feature",
      "properties": {
        "nombre": "Parque Central",
        "descripcion": "Área verde con juegos infantiles",
        "tipo": "parque",
        "area": 500,
        "color": "verde"
      },
      "geometry": {
        "type": "Polygon",
        "coordinates": [[
          [-76.123456, -12.045678],
          [-76.123456, -12.047890],
          [-76.125678, -12.047890],
          [-76.125678, -12.045678],
          [-76.123456, -12.045678]
        ]]
      }
    }
  ]
}`;

  return (
    <div className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/50 mb-4">
            <FileJson className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Documentación de Archivos GeoJSON
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Guía completa para preparar tus archivos GeoJSON y cargar lotes, marcadores, líneas y áreas recreativas a tu proyecto.
          </p>
        </div>

        {/* Nota sobre idiomas */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-8">
          <div className="flex items-start gap-3">
            <Languages className="w-6 h-6 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-purple-800 dark:text-purple-200 mb-1">Soporte Bilingüe (Español e Inglés)</h3>
              <p className="text-sm text-purple-700 dark:text-purple-300">
                Los nombres de columnas y valores pueden estar en <strong>español o inglés</strong>.
                El sistema reconoce automáticamente variaciones comunes. Por ejemplo, puedes usar <code className="bg-purple-100 dark:bg-purple-800 px-1 rounded">status</code> o <code className="bg-purple-100 dark:bg-purple-800 px-1 rounded">estado</code>,
                <code className="bg-purple-100 dark:bg-purple-800 px-1 rounded">disponible</code> o <code className="bg-purple-100 dark:bg-purple-800 px-1 rounded">available</code>.
              </p>
            </div>
          </div>
        </div>

        {/* Nota importante */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-8">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-1">Importante</h3>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Los nombres de columnas no distinguen mayúsculas/minúsculas ni acentos.
                Por ejemplo: <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">ESTADO</code>, <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">estado</code>, y <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">Estado</code> son equivalentes.
              </p>
            </div>
          </div>
        </div>

        {/* Estructura básica */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Estructura Básica de GeoJSON</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Todos los archivos deben seguir el formato GeoJSON estándar con una colección de features:
          </p>
          <CodeBlock code={`{
  "type": "FeatureCollection",
  "features": [
    {
      "type": "Feature",
      "properties": {
        // Aquí van los datos de cada elemento
      },
      "geometry": {
        // Aquí va la geometría (Point, LineString, Polygon)
      }
    }
  ]
}`} />
        </div>

        {/* Tipos de geometría */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-8">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Tipos de Geometría por Elemento</h3>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li><strong>Lotes:</strong> Polygon (polígono cerrado)</li>
                <li><strong>Marcadores:</strong> Point (punto con coordenadas)</li>
                <li><strong>Líneas:</strong> LineString (secuencia de puntos)</li>
                <li><strong>Áreas Recreativas:</strong> Polygon (polígono cerrado)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Sección de Valores Booleanos */}
        <div className="mb-8 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Valores Booleanos (Verdadero/Falso)</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Para campos como <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">corner_lot</code> (esquinero), puedes usar cualquiera de estos valores:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {booleanValues.map((item, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-3 h-3 rounded-full ${item.value === 'true' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  <span className="font-semibold text-gray-900 dark:text-white">{item.value === 'true' ? 'Verdadero' : 'Falso'}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {item.aliases.map((alias, i) => (
                    <span key={i} className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded text-xs">
                      {alias}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sección de Colores */}
        <div className="mb-8 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Colores Aceptados</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Puedes usar códigos hexadecimales (#RRGGBB) o nombres de colores en español o inglés:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {colorValues.map((item, index) => (
              <div key={index} className="flex items-center gap-2 bg-white dark:bg-gray-900 p-2 rounded-lg border border-gray-200 dark:border-gray-700">
                <span className="w-6 h-6 rounded flex-shrink-0 border border-gray-300 dark:border-gray-600" style={{ backgroundColor: item.value }}></span>
                <div className="text-xs">
                  <code className="text-gray-700 dark:text-gray-300">{item.value}</code>
                  <div className="text-gray-500">{item.aliases.join(', ')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Secciones de documentación */}
        <Accordion title="Lotes (Polygon)" icon={Square} defaultOpen={true}>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Los lotes deben ser polígonos cerrados. El sistema calculará automáticamente el área si no se proporciona.
          </p>

          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Columnas Disponibles</h4>
          <FieldTable fields={lotFields} />

          <ValueTable title="Valores de Estado (status / estado)" values={statusValues} colorDot={true} />
          <ValueTable title="Valores de Documento Legal (legal_paper / documento_legal)" values={legalPaperValues} />

          <h4 className="font-semibold text-gray-900 dark:text-white mt-6 mb-3">Ejemplo de GeoJSON para Lotes (usando español)</h4>
          <CodeBlock code={lotExample} />
        </Accordion>

        <Accordion title="Marcadores (Point)" icon={MapPin}>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Los marcadores son puntos en el mapa que indican ubicaciones importantes como entradas, salidas, etapas, etc.
          </p>

          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Columnas Disponibles</h4>
          <FieldTable fields={markerFields} />

          <ValueTable title="Tipos de Marcador (marker_type / tipo)" values={markerTypeValues} colorDot={true} />

          <h4 className="font-semibold text-gray-900 dark:text-white mt-6 mb-3">Ejemplo de GeoJSON para Marcadores (usando español)</h4>
          <CodeBlock code={markerExample} />
        </Accordion>

        <Accordion title="Líneas (LineString)" icon={Minus}>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Las líneas representan calles, veredas, límites, líneas de canchas deportivas, tuberías, etc.
          </p>

          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Columnas Disponibles</h4>
          <FieldTable fields={lineFields} />

          <ValueTable title="Tipos de Línea (line_type / tipo)" values={lineTypeValues} colorDot={true} />

          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <h5 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Estilos de Línea (line_style / estilo)</h5>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-12 h-0.5 bg-gray-800 dark:bg-gray-200"></div>
                <span className="text-gray-700 dark:text-gray-300">solid (sólida)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-12 h-0.5 border-t-2 border-dashed border-gray-800 dark:border-gray-200"></div>
                <span className="text-gray-700 dark:text-gray-300">dashed (guiones)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-12 h-0.5 border-t-2 border-dotted border-gray-800 dark:border-gray-200"></div>
                <span className="text-gray-700 dark:text-gray-300">dotted (puntos)</span>
              </div>
            </div>
          </div>

          <h4 className="font-semibold text-gray-900 dark:text-white mt-6 mb-3">Ejemplo de GeoJSON para Líneas (usando español)</h4>
          <CodeBlock code={lineExample} />
        </Accordion>

        <Accordion title="Áreas Recreativas (Polygon)" icon={Trees}>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Las áreas recreativas son polígonos que representan parques, canchas, piscinas, zonas comunes y espacios públicos obligatorios.
          </p>

          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Columnas Disponibles</h4>
          <FieldTable fields={areaFields} />

          <ValueTable title="Tipos de Área Recreativa (area_type / tipo)" values={areaTypeValues} />

          <h4 className="font-semibold text-gray-900 dark:text-white mt-6 mb-3">Ejemplo de GeoJSON para Áreas Recreativas (usando español)</h4>
          <CodeBlock code={areaExample} />
        </Accordion>

        {/* Tips finales */}
        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Consejos para Preparar tus Archivos</h3>
          <ul className="space-y-3 text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Usa las coordenadas en formato <strong>[longitud, latitud]</strong> (orden estándar de GeoJSON)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Los polígonos deben estar <strong>cerrados</strong> (el primer y último punto deben ser iguales)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Puedes usar herramientas como <strong>QGIS</strong>, <strong>geojson.io</strong> o <strong>Mapshaper</strong> para crear y validar tus archivos</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Los nombres de columnas pueden estar en <strong>español o inglés</strong> - el sistema los reconoce automáticamente</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Los valores como "disponible", "available", "DISPONIBLE" son <strong>equivalentes</strong> (no importan mayúsculas ni acentos)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Si no proporcionas un valor, el sistema usará el valor por defecto (cuando aplique)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Siempre puedes editar los valores después de cargarlos desde el dashboard</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default GeoJSONDocumentation;
